import React, { useState } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import Modal from "components/Modal/Modal";
import { Profile as ProfileType } from "../profileTypes";
import { EuiText, EuiFieldText, EuiForm, EuiFormRow, EuiSpacer } from "@elastic/eui";
import Button from "components/Buttons/Button";
import MonitoringProfilesService from "services/AdminConsole/Monitoring/Profiles/MonitoringProfilesService";

interface CopyProfileProps {
  setIsModalOpen: (isOpen: boolean) => void;
  profileToCopy: ProfileType;
  profiles: ProfileType[];
  refetch: () => void;
}

const CopyProfile: React.FC<CopyProfileProps> = ({
  setIsModalOpen,
  profileToCopy,
  profiles,
  refetch,
}) => {
  const { clientId } = useSelector(globalFilterSelector, shallowEqual);
  const [profileName, setProfileName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const getValidationErrors = () => {
    if (!profileName.trim()) {
      return ["Profile name is required"];
    }

    // Check for duplicate profile names
    const trimmedName = profileName.trim();
    const isDuplicate = profiles.some(
      (profile) => profile.name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (isDuplicate) {
      return ["A profile with this name already exists"];
    }

    return [];
  };

  const validationErrors = getValidationErrors();
  const shouldShowErrors = hasInteracted && validationErrors.length > 0;

  const handleSave = async () => {
    if (validationErrors.length > 0) return;

    setIsLoading(true);

    const params = {
      clientId,
      monitoringProfileId: profileToCopy.id,
      name: profileName.trim(),
    };

    try {
      await MonitoringProfilesService.copyProfile(params);
      refetch();
      setIsModalOpen(false);
    } catch (error) {
      console.error("Failed to copy profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setHasInteracted(true);
    if (validationErrors.length === 0) {
      handleSave();
    }
  };

  const footer = (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
      <Button onClick={handleCancel} type="secondary" text="Cancel" disabled={isLoading} />
      <Button
        onClick={handleSave}
        type="primary"
        text="Save"
        disabled={validationErrors.length > 0 || isLoading}
      />
    </div>
  );

  return (
    <Modal title={`Copy Profile`} onClose={handleClose} maxWidth={500} footer={footer}>
      <EuiText size="s">
        {`You are copying profile `}
        <span style={{ fontWeight: 600 }}>{profileToCopy.name}</span>
        {` and all of its parameters. What would you like to name this new profile?`}{" "}
      </EuiText>
      <EuiSpacer size="s" />
      <EuiForm component="form" onSubmit={handleFormSubmit}>
        <EuiFormRow
          label="Profile Name"
          isInvalid={shouldShowErrors}
          error={shouldShowErrors ? validationErrors : undefined}>
          <EuiFieldText
            placeholder="Enter name"
            value={profileName}
            onChange={(e) => {
              setProfileName(e.target.value);
              if (!hasInteracted) {
                setHasInteracted(true);
              }
            }}
            isInvalid={shouldShowErrors}
          />
        </EuiFormRow>
      </EuiForm>
    </Modal>
  );
};

export default CopyProfile;
